import { ConfigurationError, GenericError, TimeoutError } from '@cian/peperrors/shared';

import {
  EAssetContentType,
  type IModuleAsset,
  isModuleAsset,
  type IFileAsset,
  type IInlineAsset,
} from '../../../shared/assets/index.js';

/**
 * Загрузчик ассетов и модулей
 */
export interface IAssetLoader {
  /**
   * Метод для загрузки файловых ассетов и модулей
   */
  import<Exports>(asset: IModuleAsset | IFileAsset, timeout?: number): Promise<Exports>;
  /**
   * Метод для загрузки инлайн ассетов
   */
  importInline(asset: IInlineAsset): void;
  /**
   * @todo
   * Метод для фоновой загрузки ресурсов
   * preload(path: string): Promise<void>;
   */
}

/**
 * Событие готовности модуля (IModuleAsset). Срабатывает тогда, когда
 * модуль сделал экспорт
 */
export const HTML_ASSET_READY_EVENT_NAME = 'asset:ready';
/**
 * Специальное событие таймаута загрузки
 */
export const HTML_ASSET_TIMEOUT_EVENT_NAME = 'asset:timeout';

// eslint-disable-next-line @typescript-eslint/naming-convention
export interface ExportsScriptElement<Exports = void> extends HTMLScriptElement {
  exports?: Exports;
}

export class DOMAssetLoader implements IAssetLoader {
  public static createInlineScriptElement(asset: IInlineAsset<EAssetContentType.Script>) {
    const el = document.createElement('script');

    el.dataset['hash'] = asset.hash;

    const content = document.createTextNode(asset.content);

    el.append(content);

    if (asset.props) {
      for (const [attr, value] of Object.entries(asset.props)) {
        el.setAttribute(attr, typeof value === 'string' ? value : '');
      }
    }

    return el;
  }

  public static createScriptElement(asset: IFileAsset<EAssetContentType.Script>) {
    const el = document.createElement('script');

    el.src = asset.src;

    if (asset.props) {
      for (const [attr, value] of Object.entries(asset.props)) {
        el.setAttribute(attr, typeof value === 'string' ? value : '');
      }
    }

    return el;
  }

  public static createInlineLinkElement(asset: IInlineAsset<EAssetContentType.Styles>) {
    const el = document.createElement('style');

    el.dataset['hash'] = asset.hash;

    const content = document.createTextNode(asset.content);

    el.append(content);

    if (asset.props) {
      for (const [attr, value] of Object.entries(asset.props)) {
        el.setAttribute(attr, typeof value === 'string' ? value : '');
      }
    }

    return el;
  }

  public static createLinkElement(asset: IFileAsset<EAssetContentType.Styles>) {
    const el = document.createElement('link');

    el.href = asset.src;
    el.rel = 'stylesheet';
    el.type = 'text/css';

    if (asset.props) {
      for (const [attr, value] of Object.entries(asset.props)) {
        el.setAttribute(attr, typeof value === 'string' ? value : '');
      }
    }

    return el;
  }

  private readonly defaultTimeout: number;

  public constructor(defaultTimeout: number = 15000) {
    this.defaultTimeout = defaultTimeout;
  }

  public importInline(asset: IInlineAsset) {
    const el =
      asset.contentType === EAssetContentType.Script
        ? DOMAssetLoader.createInlineScriptElement(asset as IInlineAsset<EAssetContentType.Script>)
        : DOMAssetLoader.createInlineLinkElement(asset as IInlineAsset<EAssetContentType.Styles>);

    document.head.appendChild(el);
  }

  public async import<Exports = void>(asset: IFileAsset | IModuleAsset, timeout?: number): Promise<Exports> {
    const el =
      asset.contentType === EAssetContentType.Script
        ? DOMAssetLoader.createScriptElement(asset as IFileAsset<EAssetContentType.Script>)
        : DOMAssetLoader.createLinkElement(asset as IFileAsset<EAssetContentType.Styles>);

    let onReady: (e: Event) => void;
    let timer: number | NodeJS.Timeout;

    const isModule = isModuleAsset(asset) && asset.hasExports;

    return new Promise<Exports>((resolve, reject) => {
      onReady = onLoad.bind(null, resolve, reject);

      el.onerror = onReady;

      /**
       * У модулей могут быть блокирующие асинхронные операции, после которых
       * делается экспорт. Событие 'load' срабатывает на згурзку файлов и
       * синхронное выполнение кода. Таким образом нужно отдельное событие, которое
       * сообщит, что модуль готов к работе.
       * Это повторяет концепцию ES Модулей
       */
      if (isModule) {
        el.addEventListener(HTML_ASSET_READY_EVENT_NAME, onReady);
      } else {
        el.onload = onReady;
      }

      document.head.appendChild(el);

      timer = setTimeout(onReady, timeout || this.defaultTimeout, new CustomEvent(HTML_ASSET_TIMEOUT_EVENT_NAME));
    });

    function clean(hasError: boolean) {
      clearTimeout(timer);

      el.onerror = null;

      if (isModule) {
        el.removeEventListener(HTML_ASSET_READY_EVENT_NAME, onReady);
      } else {
        el.onload = null;
      }

      if (hasError) {
        /**
         * Удаление элемента делается для возможных, последующих ретраев
         */
        document.head.removeChild(el);
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    function onLoad(resolve: (assetExports?: Exports) => void, reject: (err: Error) => void, e: Event | ErrorEvent) {
      if (e.type === HTML_ASSET_TIMEOUT_EVENT_NAME) {
        /**
         * Объект события создается в setTiemout, но само событие не тригерится на элементе.
         * Для дебага и возможных реакций в будущем необходимо руками триггерить событие
         */
        el.dispatchEvent(e);

        clean(true);

        reject(
          new TimeoutError({
            message: `Failed to load asset ${asset.src}`,
            domain: 'DOMAssetLoader.import',
          }),
        );
      }

      if (e.type === 'error') {
        /**
         * В спецификации нет четких указаний какое событие возникает на 'error',
         * это может быть как просто Event, так и EventError
         */
        const err = new GenericError({
          message:
            typeof (e as ErrorEvent).error === 'string'
              ? (e as ErrorEvent).error
              : (e as ErrorEvent).error?.message || `Failed to load asset ${asset.src}`,
          type: 'AssetLoadError',
          domain: 'DOMAssetLoader.import',
        });

        if ((e as ErrorEvent).error?.name) {
          err.name = (e as ErrorEvent).error.name;
        }

        if ((e as ErrorEvent).error?.stack) {
          err.stack = (e as ErrorEvent).error.stack;
        }

        clean(true);

        reject(err);
      }

      if (isModule) {
        if ((el as ExportsScriptElement<Exports>).exports) {
          clean(false);

          resolve((el as ExportsScriptElement<Exports>).exports as Exports);
        } else {
          clean(true);

          reject(
            new ConfigurationError({
              message: `Module has no exports ${asset.src}`,
              domain: 'DOMAssetLoader.import',
            }),
          );
        }
      } else {
        clean(false);

        resolve();
      }
    }
  }
}
